import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import api from '@utils/api';
import { useI18n } from '@/i18n';
import PageHeader from '@components/PageHeader';
import { IconFile, IconDownload } from '@components/icons';

export default function MyDocuments() {
  const { t, lang } = useI18n();
  const [docs,    setDocs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy,    setBusy]    = useState(null);

  useEffect(() => {
    api.get('/documents/me')
      .then(({ data }) => setDocs(data.documents || []))
      .finally(() => setLoading(false));
  }, []);

  const download = async (id, filename) => {
    setBusy(id);
    try {
      const { data } = await api.get(`/documents/${id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url; a.download = filename || 'documento';
      a.click();
      URL.revokeObjectURL(url);
    } finally { setBusy(null); }
  };

  return (
    <>
      <PageHeader title={t('documents.title')} subtitle={t('documents.subtitle')} />
      <div className="px-5 md:px-0 pb-6">
        {loading ? (
          <div className="card p-8 text-center text-gray-400">{t('common.loading')}</div>
        ) : docs.length === 0 ? (
          <div className="card p-10 text-center">
            <div className="mx-auto w-12 h-12 grid place-items-center rounded-xl bg-gray-100 text-gray-400 mb-3">
              <IconFile size={24} />
            </div>
            <p className="text-sm text-gray-500">{t('documents.empty')}</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {docs.map((d, i) => (
              <motion.div key={d.id}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.04 }}
                className="card p-4 flex items-center gap-4">
                <span className="shrink-0 grid place-items-center w-10 h-10 rounded-xl bg-glk-blue/10 text-glk-blue">
                  <IconFile size={20} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-glk-dark truncate">
                    {t(`documents.types.${d.type}`)}
                    {d.period && <span className="ml-2 font-mono text-xs text-gray-400">{d.period}</span>}
                  </p>
                  <p className="text-xs text-gray-400 truncate">
                    {d.original_filename || '—'} · {new Date(d.created_at).toLocaleDateString(lang === 'en' ? 'en-GB' : 'pt-PT')}
                  </p>
                </div>
                <button onClick={() => download(d.id, d.original_filename)} disabled={busy === d.id}
                        className="btn-ghost !px-2.5 !py-2 shrink-0" aria-label={t('documents.download')}>
                  <IconDownload size={18} />
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
